<template>
  <div ref="chartRef" class="map-chart"></div>
</template>
<script>
import * as echarts from 'echarts';
import { getCurrentLocale } from '@/utils/languageManager';
export default {
  props: {
    isShowLine: {
      type: Boolean,
      default: true,
    },
    zoom: {
      type: Number,
      default: 1.0,
    },
    layoutCenter: {
      type: Array,
    },
    fontSize: {
      type: Number,
      default: null,
    },
    isBold: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      chartRef: null,
      chart: null,
      CN: [121.5, 31.2], //默认上海的经纬度坐标
      points: [],
      // 用于节流高亮更新，避免频繁 setOption 造成卡顿
      highlightRafId: null,
      lastHighlightedCity: null,
    };
  },
  computed: {},
  watch: {
    currentLocale(val) {
      console.log('当前语言：', val);
      //重新获取points
      this.chart = null;
      this.points = this.setPoints();
      // 语言切换时，重置高亮状态并取消节流帧
      this.lastHighlightedCity = null;
      if (this.highlightRafId) {
        cancelAnimationFrame(this.highlightRafId);
        this.highlightRafId = null;
      }
      this.initChart();
    },
  },
  mounted() {
    this.points = this.setPoints();
    this.initChart();
    window.addEventListener('resize', this.handleResize);
  },
  unmounted() {
    if (this.chart) {
      this.chart.dispose();
    }
    if (this.highlightRafId) {
      cancelAnimationFrame(this.highlightRafId);
      this.highlightRafId = null;
    }
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    handleResize() {
      if (this.chart) {
        this.chart.resize();
      }
    },
    setPoints() {
      let list = [
        {
          name: this.currentLocale === 'zh' ? '新疆' : 'Xinjiang',
          coord: [87.6, 43.8],
        },
        {
          name: this.currentLocale === 'zh' ? '香港' : 'Hong Kong',
          coord: [114.1, 22.3],
        },
        {
          name: this.currentLocale === 'zh' ? '塔什干' : 'Tashkent',
          coord: [69.2, 41.3],
        },
        {
          name: this.currentLocale === 'zh' ? '阿布扎比' : 'Abu Dhabi',
          coord: [54.4, 24.5],
        },
        {
          name: this.currentLocale === 'zh' ? '柔佛' : 'Johor',
          coord: [103.7, 1.5],
        },
        {
          name: this.currentLocale === 'zh' ? '天津' : 'Tianjin',
          coord: [117.2, 39.1],
        },
        {
          name: this.currentLocale === 'zh' ? '得克萨斯州' : 'Texas',
          coord: [-99.9, 31.0],
        },
        {
          name: this.currentLocale === 'zh' ? '墨西哥城' : 'Mexico City',
          coord: [-99.1332, 19.4326],
        },
      ];
      return list;
    },
    //设置城市高亮脉冲效果
    setCityHighlight(cityName) {
      if (!this.chart) return;
      // 相同城市重复调用时，直接跳过
      if (cityName === this.lastHighlightedCity) return;
      this.lastHighlightedCity = cityName || null;

      if (this.highlightRafId) {
        cancelAnimationFrame(this.highlightRafId);
        this.highlightRafId = null;
      }
      this.highlightRafId = requestAnimationFrame(() => {
        if (!this.chart) return;
        const selectedName = this.lastHighlightedCity;
        const data = this.points.map((p) => ({
          name: p.name,
          value: p.coord,
          itemStyle: {
            color: !selectedName
              ? 'rgba(0,0,0,0)'
              : p.name === selectedName || String(selectedName).includes(p.name)
              ? '#ffd166'
              : 'rgba(0,0,0,0)',
          },
        }));
        this.chart.setOption(
          { series: [{ id: 'cityRipples', data }] },
          false,
          true
        );
      });
    },
    async initChart() {
      if (this.chart) return;

      this.chart = echarts.init(this.$refs.chartRef);
      // 优先加载本地 world.json，失败则回退到 CDN
      const loadWorld = async () => {
        try {
          const r = await fetch('/js/word.json', { cache: 'no-store' });
          if (!r.ok) throw new Error('local missing');
          return await r.json();
        } catch (e) {
          const r = await fetch(
            'https://fastly.jsdelivr.net/npm/echarts@5/map/json/world.json'
          );
          return await r.json();
        }
      };
      const world = await loadWorld();
      echarts.registerMap('world', world);
      // 地理点位数据 - 只显示指定地点
      const CN = this.CN; // 上海作为发射起点
      // const points = [
      //   { name: '新疆', coord: [87.6, 43.8] }, // 新疆乌鲁木齐
      //   { name: '香港', coord: [114.1, 22.3] }, // 香港
      //   { name: '塔什干', coord: [69.2, 41.3] }, // 乌兹别克斯坦塔什干
      //   { name: '阿布扎比', coord: [54.4, 24.5] }, // 阿联酋阿布扎比
      //   { name: '柔佛', coord: [103.7, 1.5] }, // 马来西亚柔佛
      //   { name: '天津', coord: [117.2, 39.1] }, // 天津
      //   { name: '得克萨斯州', coord: [-99.9, 31.0] }, // 美国得克萨斯州
      //   { name: '墨西哥城', coord: [-99.1332, 19.4326] }, // 墨西哥城
      // ];

      // 生成飞线数据
      const lineData = this.points.map((p) => ({ coords: [CN, p.coord] }));

      const option = {
        backgroundColor: 'transparent',

        geo: {
          map: 'world',
          roam: false,
          zoom: this.zoom,
          center: [0, 0],
          layoutCenter: this.layoutCenter ? this.layoutCenter : ['50%', '50%'],
          layoutSize: '100%',
          itemStyle: {
            areaColor: '#0c3a8a',
            borderColor: '#66aaff',
            borderWidth: 0.8,
          },
          // disabled: true // 禁用交互
          emphasis: { itemStyle: { areaColor: '#124a9d' }, disabled: true },
        },
        series: [
          // 地图上"点位"标签（白字）
          {
            type: 'scatter',
            coordinateSystem: 'geo',
            symbolSize: 6,
            itemStyle: { color: '#fff' },
            label: {
              show: true,
              color: '#fff',
              position: 'right',
              distance: 6,
              fontSize: this.fontSize ? this.fontSize : 12,
              fontWeight: this.isBold ? 'bold' : '',
              formatter: (p) => p.name,
            },
            // data: this.points.map((p) => ({ name: p.name, value: p.coord })),

            data: this.points.map((p) => ({
              name: p.name,
              value: p.coord,
              label:
                p.name === '新疆' || p.name === 'Xinjiang'
                  ? {
                      position: 'left',
                      distance: 6,
                      offset: this.currentLocale === 'zh' ? [0, -8] : [70, -8],
                    }
                  : ['塔什干', '阿布扎比'].includes(p.name)
                  ? { position: 'left', distance: 6 }
                  : undefined,
            })),
            silent: false,
          },
          // 背景虚线（静态，模仿截图里的白色短划线）
          {
            type: 'lines',
            coordinateSystem: 'geo',
            zlevel: 1,
            effect: { show: false },
            lineStyle: {
              color: '#ffffff',
              width: 1.6,
              opacity: 0.9,
              type: 'dashed',
              curveness: 0.22,
              cap: 'round',
            },
            data: this.isShowLine ? lineData : [],
          },
          // 叠一层轻微发光（更贴近 UI 质感）
          {
            type: 'lines',
            coordinateSystem: 'geo',
            zlevel: 2,
            effect: { show: false },
            lineStyle: {
              color: '#bfe5ff',
              width: 1,
              opacity: 0.35,
              shadowColor: '#7fd1ff',
              shadowBlur: 10,
              curveness: 0.22,
            },
            data: this.isShowLine ? lineData : [],
          },
          // 流光点（从中国向外跑）
          {
            type: 'lines',
            coordinateSystem: 'geo',
            zlevel: 3,
            effect: {
              show: true,
              symbol: 'circle',
              symbolSize: 6,
              color: '#ffffff',
              trailLength: 0.55,
              constantSpeed: 110,
            },
            lineStyle: { width: 0, opacity: 0, curveness: 0.22 },
            data: this.isShowLine ? lineData : [],
          },
          // 上海汇聚点的脉冲
          {
            type: 'effectScatter',
            coordinateSystem: 'geo',
            zlevel: 4,
            rippleEffect: { scale: 4, brushType: 'stroke' },
            itemStyle: { color: '#7fd1ff' },
            symbolSize: 10,

            data: [{ value: CN, name: '上海' }],
            silent: false,
          },
          // 上海标签
          {
            type: 'scatter',
            coordinateSystem: 'geo',
            symbolSize: 0,
            itemStyle: { color: 'transparent' },
            label: {
              show: true,
              position: 'right',
              distance: 6,
              color: '#7fd1ff',
              fontSize: 14,
              fontWeight: 'bold',
              formatter: this.currentLocale === 'zh' ? '上海' : 'Shanghai',
              textShadowColor: 'rgba(0, 0, 0, 0.8)',
              textShadowBlur: 4,
            },
            data: [{ value: CN, name: '上海' }],
          },
          // 城市脉冲：默认全部透明，仅选中项高亮
          {
            id: 'cityRipples',
            type: 'effectScatter',
            coordinateSystem: 'geo',
            zlevel: 5,
            rippleEffect: { scale: 4, brushType: 'stroke' },
            symbolSize: 10,
            data: this.points.map((p) => ({
              name: p.name,
              value: p.coord,
              itemStyle: { color: 'rgba(0,0,0,0)' },
            })),
            silent: false,
          },
        ],
      };
      this.chart.setOption(option);
    },
  },
};
</script>
<style lang="scss" scoped>
.map-chart {
  width: 100%;
  height: 100%;
}
</style>
